import React, { useState, useEffect } from 'react';
import { Plus, List, Home, LogOut, Wallet, ScanLine, PieChart, User, AlertCircle, Loader2, Save, X, Eye, EyeOff, Globe, Settings as SettingsIcon } from 'lucide-react';
import { ExpenseForm } from './components/ExpenseForm';
import { ExpenseList } from './components/ExpenseList';
import { TopPage } from './components/TopPage';
import { LoginForm } from './components/LoginForm';
import { Settings } from './components/Settings';
import { Transaction } from './types';
import { userService } from './services/userService';
import { storageService } from './services/storageService';
import { authService } from './services/authService';

enum View {
  HOME = 'HOME',
  FORM = 'FORM',
  LIST = 'LIST',
}

const STORAGE_KEY_USER_ID = 'smart_ledger_user_id';

export default function App() {
  const [currentView, setCurrentView] = useState<View>(View.HOME);
  
  // Initialize userId from localStorage if available
  const [userId, setUserId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_USER_ID);
    } catch (e) {
      console.error("Failed to read from localStorage", e);
      return null;
    }
  });

  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Guest Warning Modal State
  const [guestAction, setGuestAction] = useState<{ run: () => Promise<void> } | null>(null);
  const [isGuestActionProcessing, setIsGuestActionProcessing] = useState(false);

  // Balance Preference Modal State
  const [showBalancePrompt, setShowBalancePrompt] = useState(false);
  const [isSavingPreference, setIsSavingPreference] = useState(false);

  // Refresh Key to force TopPage to reload data without window.reload
  const [refreshKey, setRefreshKey] = useState(0);

  // Token Checking State
  const [isTokenChecking, setIsTokenChecking] = useState(() => {
    // Initial state depends on whether a token is present in the URL
    return !!new URLSearchParams(window.location.search).get('token');
  });

  // Handle Handover Token
  useEffect(() => {
    const checkToken = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token');
      
      if (token && token.trim() !== '') {
        // Only set checking to true if we haven't already decided (though initial state handles this)
        setIsTokenChecking(true);
        try {
          const validUserId = await authService.validateHandoverToken(token.trim());
          if (validUserId) {
            handleLoginSuccess(validUserId);
            // Clean up the URL to remove token, keeping path and other potential params if needed (usually just clear query)
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          } else {
             console.warn("Invalid token or user not found");
          }
        } catch (e) {
          console.error("Auto-login failed:", e);
        } finally {
          setIsTokenChecking(false);
        }
      } else {
        setIsTokenChecking(false);
      }
    };

    checkToken();
  }, []);

  // Check for balance preference on load/login
  useEffect(() => {
    const checkPreference = async () => {
      if (userId && userId !== 'guest') {
        const prefs = await userService.getUserPreferences(userId);
        // If preference hasn't been set yet (document missing or flag false)
        if (!prefs || !prefs.hasSetBalancePreference) {
          setShowBalancePrompt(true);
        }
      }
    };
    if (!isTokenChecking) {
      checkPreference();
    }
  }, [userId, isTokenChecking]);

  const handleLoginSuccess = (username: string) => {
    setUserId(username);
    try {
      localStorage.setItem(STORAGE_KEY_USER_ID, username);
    } catch (e) {
      console.error("Failed to save to localStorage", e);
    }
  };

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true);
  };

  const performLogout = () => {
    setUserId(null);
    setCurrentView(View.HOME);
    setEditingTransaction(null);
    setIsLogoutConfirmOpen(false);
    setShowBalancePrompt(false);
    try {
      localStorage.removeItem(STORAGE_KEY_USER_ID);
    } catch (e) {
      console.error("Failed to remove from localStorage", e);
    }
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setCurrentView(View.FORM);
  };

  const handleFormViewChange = () => {
    setEditingTransaction(null);
    setCurrentView(View.LIST);
  };

  const handleGuestConfirmation = (action: () => Promise<void>) => {
    setGuestAction({ run: action });
  };

  const executeGuestAction = async () => {
    if (!guestAction) return;
    setIsGuestActionProcessing(true);
    try {
        await guestAction.run();
        setGuestAction(null);
    } catch (e) {
        console.error(e);
        setGuestAction(null); 
    } finally {
        setIsGuestActionProcessing(false);
    }
  };

  const handleBalancePreference = async (enable: boolean) => {
    if (!userId) return;
    setIsSavingPreference(true);
    try {
        let currentBalance = 0;
        
        // If enabling, calculate current balance to store initially in Account DB
        if (enable) {
            const transactions = await storageService.getTransactions(userId);
            const expenses = transactions.filter(t => t.type === 'expense' || t.type === 'charge');
            const incomes = transactions.filter(t => t.type === 'income');
            const totalSpent = expenses.reduce((sum, e) => sum + e.amount, 0);
            const totalIncome = incomes.reduce((sum, i) => sum + i.amount, 0);
            currentBalance = totalIncome - totalSpent;
        }

        // Save to Account Database (Portal Setting)
        await userService.updateUserPreferences(userId, {
            displayTotalBalance: enable,
            hasSetBalancePreference: true,
            cachedTotalBalance: enable ? currentBalance : 0
        });
        
        setShowBalancePrompt(false);
        // Force TopPage to reload its data by updating key instead of reloading the page
        setRefreshKey(prev => prev + 1);
    } catch (e) {
        console.error("Failed to save preference", e);
        alert("設定の保存に失敗しました。");
    } finally {
        setIsSavingPreference(false);
    }
  };

  const renderContent = () => {
    if (!userId) return null;

    switch (currentView) {
      case View.HOME:
        // Pass refreshKey to force re-mount/re-fetch when settings change
        return <TopPage key={refreshKey} userId={userId} />;
      case View.FORM:
        return <ExpenseForm 
            onViewChange={handleFormViewChange} 
            userId={userId} 
            initialData={editingTransaction}
            onCheckGuest={handleGuestConfirmation}
        />;
      case View.LIST:
        return <ExpenseList 
            userId={userId} 
            onEdit={handleEditTransaction}
        />;
      default:
        return <TopPage key={refreshKey} userId={userId} />;
    }
  };

  // Loading State for Token Check
  if (isTokenChecking) {
    return (
      <div className="h-[100dvh] w-full flex flex-col items-center justify-center paper-bg gap-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{background:'var(--forest)'}}>
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
        <p className="text-sm font-medium animate-pulse" style={{color:'var(--ink-light)'}}>アカウントを確認中...</p>
      </div>
    );
  }

  // Login View
  if (!userId) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  const isGuest = userId === 'guest';

  // Main App View
  return (
    <div className="h-[100dvh] w-full max-w-md mx-auto overflow-hidden relative flex flex-col animate-fade-in" style={{background:'var(--cream)'}}>
      {/* Header */}
      <header className="px-5 py-3 sticky top-0 z-20 flex items-center justify-between shrink-0 animate-fade-down" style={{background:'rgba(250,247,242,0.9)', backdropFilter:'blur(12px)', borderBottom:'1px solid rgba(44,36,22,0.07)'}}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center hover-lift animate-bounce-in" style={{background:'var(--forest)'}}>
            <Wallet className="w-4 h-4 text-white" />
          </div>
          <div className="stagger-child animate-slide-right delay-100">
            <h1 className="font-display text-base font-600 leading-none" style={{color:'var(--ink)'}}>Smart Ledger</h1>
            {isGuest && <span className="text-[9px] font-semibold tracking-widest uppercase" style={{color:'var(--ink-muted)'}}>Guest</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 stagger-child animate-slide-left delay-100">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300 hover:rotate-90"
            style={{background:'var(--cream-dark)', color:'var(--ink-light)'}}
            title="設定"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleLogoutClick}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
            style={{background:'var(--cream-dark)', color:'var(--ink-light)'}}
            title="ログアウト"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="p-4 pb-28">
          {renderContent()}
        </div>
      </main>

      {/* Bottom Navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bottom-nav px-6 py-3 flex items-center justify-between animate-nav">
        <button
          onClick={() => setCurrentView(View.HOME)}
          className={`flex flex-col items-center gap-0.5 transition-all duration-300 px-4 py-2 rounded-xl ripple-container ${
            currentView === View.HOME ? 'nav-active' : 'nav-inactive'
          }`}
          style={{transform: currentView === View.HOME ? 'scale(1.08)' : 'scale(1)'}}
        >
          <PieChart className="w-5 h-5 transition-transform duration-300" />
          <span className="text-[10px] font-semibold">ホーム</span>
          {currentView === View.HOME && <div className="nav-dot" />}
        </button>

        {/* FAB */}
        <div className="relative -mt-5">
          <button
            onClick={() => { setEditingTransaction(null); setCurrentView(View.FORM); }}
            className={`fab w-14 h-14 flex items-center justify-center relative ${currentView === View.FORM ? 'active-form animate-glow' : 'fab-ring'}`}
          >
            <div style={{transition:'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)', transform: currentView === View.FORM ? 'rotate(45deg)' : 'rotate(0deg)'}}>
              {currentView === View.FORM
                ? <ScanLine className="w-6 h-6 text-white" />
                : <Plus className="w-7 h-7 text-white" />}
            </div>
          </button>
        </div>

        <button
          onClick={() => setCurrentView(View.LIST)}
          className={`flex flex-col items-center gap-0.5 transition-all duration-300 px-4 py-2 rounded-xl ripple-container ${
            currentView === View.LIST ? 'nav-active' : 'nav-inactive'
          }`}
          style={{transform: currentView === View.LIST ? 'scale(1.08)' : 'scale(1)'}}
        >
          <List className="w-5 h-5 transition-transform duration-300" />
          <span className="text-[10px] font-semibold">明細</span>
          {currentView === View.LIST && <div className="nav-dot" />}
        </button>
      </div>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center modal-backdrop animate-fade-in">
          <div className="w-full max-w-md h-[88vh] overflow-hidden rounded-t-3xl flex flex-col animate-slide-up" style={{background:'var(--cream)'}}>
            <Settings
              userId={userId}
              onClose={() => { setIsSettingsOpen(false); setRefreshKey(prev => prev + 1); }}
            />
          </div>
        </div>
      )}

      {/* Balance Preference Modal */}
      {showBalancePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 modal-backdrop animate-fade-in">
          <div className="card w-full max-w-sm p-6 space-y-5 animate-scale-bounce overflow-hidden">
            <div className="flex flex-col items-center text-center space-y-3 pt-1">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center animate-float" style={{background:'var(--forest-pale)'}}>
                <Globe className="w-6 h-6" style={{color:'var(--forest)'}} />
              </div>
              <div>
                <h3 className="font-display font-600 text-lg mb-1" style={{color:'var(--ink)'}}>ポータル連携設定</h3>
                <p className="text-sm leading-relaxed" style={{color:'var(--ink-light)'}}>
                  ポータルへの残高表示を許可しますか？
                </p>
                <p className="text-xs mt-2 px-3 py-2 rounded-xl animate-slide-down delay-100 stagger-child" style={{background:'var(--terra-pale)', color:'var(--terra)'}}>
                  「非表示」を選ぶと、アプリ上でも残高が ****表示になります
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleBalancePreference(false)}
                disabled={isSavingPreference}
                className="py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 active:scale-95 ripple-container"
                style={{background:'var(--cream-dark)', color:'var(--ink-light)'}}
              >
                <EyeOff className="w-4 h-4" /> 非表示
              </button>
              <button
                onClick={() => handleBalancePreference(true)}
                disabled={isSavingPreference}
                className="btn-forest py-3 text-sm flex items-center justify-center gap-2 ripple-container"
              >
                {isSavingPreference ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                許可する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Warning Modal */}
      {guestAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in">
          <div className="card w-full max-w-sm p-6 space-y-4 animate-scale-bounce">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-heartbeat" style={{background:'var(--gold-pale)'}}>
                <AlertCircle className="w-5 h-5" style={{color:'var(--gold)'}} />
              </div>
              <h3 className="font-semibold text-base" style={{color:'var(--ink)'}}>未ログインでの保存</h3>
            </div>
            <p className="text-sm leading-relaxed" style={{color:'var(--ink-light)'}}>
              ゲストモードではデータはブラウザにのみ保存されます。キャッシュ削除でデータが消える可能性があります。このまま保存しますか？
            </p>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={() => setGuestAction(null)}
                className="py-3 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
                style={{background:'var(--cream-dark)', color:'var(--ink-light)'}}
              >
                キャンセル
              </button>
              <button
                onClick={executeGuestAction}
                disabled={isGuestActionProcessing}
                className="btn-terra py-3 text-sm flex items-center justify-center gap-2 ripple-container"
              >
                {isGuestActionProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {isLogoutConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in">
          <div className="card w-full max-w-sm p-6 space-y-4 animate-scale-bounce">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-swing" style={{background:'var(--terra-pale)'}}>
                  <LogOut className="w-5 h-5" style={{color:'var(--terra)'}} />
                </div>
                <h3 className="font-semibold text-base" style={{color:'var(--ink)'}}>ログアウト確認</h3>
              </div>
              <button
                onClick={() => setIsLogoutConfirmOpen(false)}
                className="transition-transform duration-200 hover:rotate-90"
                style={{color:'var(--ink-muted)'}}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm leading-relaxed" style={{color:'var(--ink-light)'}}>
              ログアウトしてもよろしいですか？入力中のデータは破棄されます。
            </p>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={() => setIsLogoutConfirmOpen(false)}
                className="py-3 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
                style={{background:'var(--cream-dark)', color:'var(--ink-light)'}}
              >
                戻る
              </button>
              <button
                onClick={performLogout}
                className="btn-terra py-3 text-sm font-semibold rounded-xl ripple-container transition-all duration-200 hover:scale-105 active:scale-95"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
